import { useQuery } from '@tanstack/react-query';
import { PkButton } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import parseHtmlToReact from 'html-react-parser';
import { memo, useMemo } from 'react';

import { getEditorialByTagQuery } from '@queries/editorial';
import { withErrorBoundary } from '@shared/ErrorBoundary';
import { useTranslateFn } from '@shared/Translate';
import { language } from '@utilities/browser';
import { stripHtml } from '@utilities/dom';
import { competitionCode } from '@utilities/WindowContext';

import * as styles from './styles.module.scss';

const TICKETING_HEADER_TAG = `${competitionCode}.TICKETING.FAQ`.toUpperCase();
const MAX_PARAGRAPHS = 3;

const Faq = () => {
  const t = useTranslateFn();
  const { data: article } = useQuery({
    ...getEditorialByTagQuery(TICKETING_HEADER_TAG),
    placeholderData: {},
  });

  const paragraphs: any[][] = useMemo(
    () =>
      (article.body as any[])
        ?.filter((elem) => ['HEADING', 'PARAGRAPH'].includes(elem._type))
        .filter((e) => stripHtml(e._html))
        .reduce((acc, el) => {
          if (el._type === 'HEADING') acc.push([el]);
          else acc.at(-1)?.push(el);

          return acc;
        }, [])
        .splice(0, MAX_PARAGRAPHS) ?? [],
    [article.body],
  );

  const button = useMemo(
    () => article.document?.content?.find((elem) => elem._attributes?.['emk-type'].includes('button')),
    [article.document?.content],
  );

  if (!article.body) return null;

  return (
    <div className='pk-bg--elevation-01 pk-py--l pk-py-md--xl2 pk-container'>
      <div className={clsx('pk-col', 'pk-col--span-12', 'pk-d--flex', 'pk-flex--column', 'pk-gap--l')}>
        <h2 className='pk-text--text-01 pk-font--comp-bold  pk-font-size--xl pk-font-size-sm--xl2'>{article.title}</h2>
        <div className={styles.grid}>
          {paragraphs.map((tuple, i) => (
            <div className='pk-d--flex pk-flex--column pk-gap--xs pk-gap-sm--s' key={`paragraph_${i}`}>
              {tuple.map((elem, j) => (
                <div
                  key={`paragraph_${i}_${j}`}
                  className={clsx({
                    'pk-text--text-01 pk-line-height--md': true,
                    'pk-font-size--m pk-font--base-regular': elem._type === 'PARAGRAPH',
                    'pk-font-size--l pk-font--base-semibold': elem._type === 'HEADING',
                  })}
                >
                  {parseHtmlToReact(elem._html ?? '')}
                </div>
              ))}
            </div>
          ))}
        </div>
        {button?._text && (
          <div className='pk-d--flex pk-flex--wrap'>
            <PkButton appearance='outlined' linkHref={button._attributes.href} linkTarget='_blank' width='auto'>
              {button._text}
            </PkButton>
          </div>
        )}
        {!['en', 'de'].includes(language) && (
          <div className='pk-font-size--s pk-font--base-regular pk-text--text-02 pk-line-height--md'>
            {t('ticketing_faq_not_translated')}
          </div>
        )}
      </div>
    </div>
  );
};

export default withErrorBoundary(memo(Faq));
